/**
 * SIGNED PHOTO LINKS.
 *
 * The job-photos bucket was public, so a photo's URL was the photo: anyone
 * holding one could open an arrival shot with the plate, the driveway and
 * the house in it, and every shop on this system could read every other
 * shop's pictures. The bucket goes private once every reader asks for a
 * signed link instead, and this file is how the desk asks.
 *
 * A signed link works for an hour. The desk hands the same one out again
 * for fifty minutes and then asks afresh, so a page left open all morning
 * never draws an image whose link has already run out.
 *
 * BATCHED. A photo grid asks for forty links at once. Every request made in
 * the same tick is collected and sent as one call, rather than forty round
 * trips on a technician's cell data.
 *
 * A photo that cannot be signed resolves to "" and says so in the console,
 * naming the path. The callers draw a "would not load" tile for "", never a
 * broken-image glyph, so a failure is visible rather than a blank.
 */

import { db } from "./client.ts";

const BUCKET = "job-photos";
const LIVE_FOR = 60 * 60;            // seconds a signed link works
const REUSE_FOR = 50 * 60 * 1000;    // ms the same link is handed out again

type Hit = { url: string; until: number };
const links = new Map<string, Hit>();
let waiting = new Map<string, Array<(url: string) => void>>();
let queued = false;

/** A link that is still good, or null. Synchronous, for markup drawn at once. */
export function freshLink(key: string): string | null {
  const hit = links.get(key);
  return hit && hit.until > Date.now() ? hit.url : null;
}

/** A signed link to the full photo at `path`, or "" if it could not be signed. */
export function signedPhoto(path: string): Promise<string> {
  const known = freshLink(path);
  if (known) return Promise.resolve(known);
  return new Promise((resolve) => {
    const list = waiting.get(path);
    if (list) list.push(resolve); else waiting.set(path, [resolve]);
    if (!queued) { queued = true; setTimeout(flush, 0); }
  });
}

async function flush(): Promise<void> {
  const batch = waiting;
  waiting = new Map();
  queued = false;

  const got = new Map<string, string>();
  const paths = [...batch.keys()];
  try {
    const sb = await db();
    for (let i = 0; i < paths.length; i += 100) {
      const { data, error } = await sb.storage.from(BUCKET).createSignedUrls(paths.slice(i, i + 100), LIVE_FOR);
      if (error) throw error;
      for (const d of data ?? []) if (d.path && d.signedUrl && !d.error) got.set(d.path, d.signedUrl);
    }
  } catch (e) {
    console.warn(`photo links: could not sign ${paths.length} photo(s)`, e);
  }

  const until = Date.now() + REUSE_FOR;
  for (const [path, resolvers] of batch) {
    const url = got.get(path) ?? "";
    if (url) links.set(path, { url, until });
    else console.warn(`photo links: no signed link for ${path}`);
    for (const r of resolvers) r(url);
  }
}

/** The key a thumbnail is remembered under, for `freshLink`. */
export const thumbKey = (path: string, size: number) => `${path}#${size}`;

/**
 * WHERE A PHOTO'S THUMBNAIL LIVES. Beside the photo, same folder, so the
 * shop-scoped storage policy (first folder is the account) covers it with
 * no change: `<account>/<job>/arrival/123.jpg` has `123.thumb.jpg`.
 */
export const thumbPathOf = (path: string): string => path.replace(/\.[^./]+$/, "") + ".thumb.jpg";

/**
 * A signed link to the small square of the photo at `path`, or "".
 *
 * NO RESIZING ON STORAGE. This used to ask storage's render endpoint for a
 * 144px crop, which Supabase bills per photo: 172 photos in the first day
 * of the 2026-10-15 cycle against 100 included, and with the spend cap on
 * an over-limit render is refused. The phone now makes the square when the
 * photo goes up (`makeThumb` in photos.ts) and stores it beside the photo,
 * and older photos were backfilled by tools/backfill-photo-thumbs.mjs. So
 * this is a plain signed link, batched with every other one in the tick.
 * A photo with no square yet falls back to the full photo, which is heavier
 * but never a blank.
 */
export async function signedThumb(path: string, size: number): Promise<string> {
  const key = thumbKey(path, size);
  const known = freshLink(key);
  if (known) return known;
  const url = (await signedPhoto(thumbPathOf(path))) || (await signedPhoto(path));
  if (url) links.set(key, { url, until: Date.now() + REUSE_FOR });
  return url;
}
